// A directory of loose files as the pack accessors OGSBranch takes — docs/OGS_EYES.md.

import { fetchAssetBytes, fetchAssetJson } from "./fetchAsset.ts";

export interface BundleBytes {
  /** Resident bytes, or undefined for a name that was never requested. */
  getBytes: (name: string) => Uint8Array | undefined;
  /** Lazy fetch for a file the eager pass skipped (the fp32 decoder fallback). */
  fetchBytes: (name: string) => Promise<Uint8Array>;
  totalBytes: number;
}

// Loud on a 404, never a stub session: an HTML error page reaches ORT as a corrupt model.
const fetchOne = (base: string, name: string): Promise<Uint8Array> =>
  fetchAssetBytes(`${base}/${name}`, "ogs bundle");

/** Fetches every name up front — the decoders are all needed within one frame of each other. */
export async function loadBundleBytes(base: string, names: string[]): Promise<BundleBytes> {
  const resident = new Map<string, Uint8Array>();
  const unique = [...new Set(names)];
  const loaded = await Promise.all(unique.map((name) => fetchOne(base, name)));
  unique.forEach((name, index) => resident.set(name, loaded[index]));
  return {
    getBytes: (name) => resident.get(name),
    fetchBytes: async (name) => {
      const already = resident.get(name);
      if (already) return already;
      const bytes = await fetchOne(base, name);
      resident.set(name, bytes);
      return bytes;
    },
    totalBytes: loaded.reduce((total, bytes) => total + bytes.byteLength, 0),
  };
}

export function fetchBundleJson(base: string, name: string): Promise<unknown> {
  return fetchAssetJson(`${base}/${name}`, "ogs bundle");
}
